/* ══════════════════════════════════════════════════════════════════════
   Trasse Console — data contract + demo data.
   The view (TrasseConsole) renders purely from a `ConsoleData` object so the
   exact same markup is driven by demo data (for design verification) or by
   live Firestore/console data (via useConsoleData). Field names & shapes are
   ported 1:1 from the approved design.
   ══════════════════════════════════════════════════════════════════════ */

import { ADDON_PROVIDERS } from '../components/addonProviders';

export type Delta = { label: string; up: boolean };

export interface Kpi { label: string; value: string; d: string; delta: string; up: boolean; note: string; }

export interface RevenueSeries {
  points: number[];      // one per month, arbitrary scale (higher = more)
  months: string[];
  total: string;         // e.g. "2.84M"
  deltaLabel: string;    // e.g. "▲ 18.4% vs last year"
  up: boolean;
}

export interface PlanMix { premium: number; standard: number; trial: number; activeSchools: number; }

export interface SchoolRev { name: string; amt: string; pct: number; }

export interface Activity { title: string; time: string; amt: string; c: string; pos?: boolean; d: string; }

export type OrgStatus = 'Active' | 'Trial' | 'Pending' | 'Expired' | 'Suspended' | 'Canceling';

/* per-org add-on connection (drives the org drilldown + API page) */
export type AddonKey = 'gps' | 'fuel' | 'fees' | 'sms';
export type ConnStatus = 'connected' | 'error' | 'disconnected';
export interface AddonConn { status: ConnStatus; provider?: string; endpoint?: string; }

export interface Org {
  name: string; code: string; plan: string; status: OrgStatus;
  /** From the org doc: true while a Paystack subscription is charging, false when there is none
   *  to cancel, null when the document does not say (older orgs — the status label decides). */
  hasActiveSubscription?: boolean | null;
  students: string; buses: string; mrr: string;
  /** Firestore productType (FULL_FEATURE, FAMILIES, CARGO, …). Used to group the Organizations list. */
  productType?: string | null;
  end?: string;                          // renewal / expiry date, formatted
  /** True when the school has asked to cancel: `end` is a CLOSING date, not a renewal. */
  canceling?: boolean;
  beta?: boolean;                        // console-provisioned, not billed
  conns?: Partial<Record<AddonKey, AddonConn>>;
  /** Campus home coordinate (geofencing anchor), or null until set. */
  homeLocations?: { lat: number; lng: number } | null;
  /** Invariant: locationSetupPending === !homeLocations. */
  locationSetupPending?: boolean;
  /** Monthly Claude cap in USD (0–100). Null means the engine’s $15 default. */
  zanaCapUsd?: number | null;
}

export type AdminStatus = 'Active' | 'Pending' | 'Inactive';
export interface Admin { name: string; email: string; phone: string; role: string; status: AdminStatus; }
export interface AdminGroup { school: string; tag: string; admins: Admin[]; }

export interface Bus { plate: string; route: string; status: string; driver: string; kids: string; rating: number; c: string; }
export interface BusGroup { school: string; code: string; buses: Bus[]; }

export interface Lorry { plate: string; model: string; cargo: string; status: string; fuel: string; dist: string; c: string; }
export interface MiniStat { k: string; v: string; }

export interface CalEvent { mon: string; day: string; title: string; meta: string; c: string; }
export interface CalendarData {
  monthLabel: string;          // "July 2026"
  daysInMonth: number;
  firstWeekdayOffset: number;  // 0 = Monday
  today: number | null;        // day-of-month if current month, else null
  eventColors: Record<number, string>; // day → color var
  events: CalEvent[];
}

export interface ContentItem { title: string; meta: string; ph: string; }

export interface FuelSchool { name: string; v: string; pct: number; good: boolean; }
export interface DriverRating { name: string; v: number; drivers: string; }
export interface RevLost { name: string; v: string; pct: number; }
export interface DistancePair { last: number; thisM: number; } // 0..1

/** `orgCode` opens that organization; `tab` opens a console tab. A notification
 *  with neither is an announcement with nowhere to go — avoid creating those:
 *  rows LOOK clickable, and a tap that does nothing reads as "notifications
 *  are broken" (Gabriel, bug 81's console half, 2026-08-06). */
export interface Notif { title: string; time: string; c: string; d: string; orgCode?: string; tab?: string; }

/* A school whose fuel costs are being worked out from an estimate rather than a
 * live EPRA ceiling or its own station list. Mirrors classifyOrgPrices() in
 * pear-payments-backend/src/engine/fuel/opsPriceAlert.js — the backend emails
 * this same list, so the two must agree on who counts as affected. */
export type EstimatedPriceKind = 'missing' | 'regional_default' | 'carried_forward' | 'basic_no_stations';
export interface EstimatedPriceOrg {
  code: string;
  name: string;
  plan: string;
  kind: EstimatedPriceKind;
  detail: string;
  /** Premium was promised an EPRA ceiling; Basic was not. Different fixes. */
  promisedEpra: boolean;
}

/* PEAR-ops incident (backend-written). Fuel-from-press updates land here so the
 * console shows a critical “review this” without blocking the auto-apply. */
export interface PlatformAlert {
  id: string;
  kind: string;
  severity: string;
  title: string;
  message: string;
  caps?: { diesel?: number | null; petrol?: number | null } | null;
  sources?: Array<{ name: string; url: string; caps?: { diesel?: number | null; petrol?: number | null } }>;
  acknowledged: boolean;
  createdAtMs?: number | null;
}

export interface ConsoleData {
  loading: boolean;
  kpis: Kpi[];
  revenue: RevenueSeries;
  planMix: PlanMix;
  schoolRev: SchoolRev[];
  activity: Activity[];
  orgs: Org[];
  adminGroups: AdminGroup[];
  busGroups: BusGroup[];
  lorries: Lorry[];
  lorryMiniStats: MiniStat[];
  calendar: CalendarData;
  contentItems: ContentItem[];
  anaKpis: Kpi[];
  fuelPerSchool: FuelSchool[];
  driverRatings: DriverRating[];
  revLost: RevLost[];
  distancePairs: DistancePair[];
  distanceTotalLabel: string;
  revLostTotal: string;
  notifs: Notif[];
  estimatedPriceOrgs: EstimatedPriceOrg[];
  platformAlerts: PlatformAlert[];
  user: { name: string; role: string; initials: string };
}

/* ── add-on / integration catalog (org drilldown → API connection) ──
   gps/fuel/fees are backend-wired (saveAddonCredentials + testAddonConnection);
   their provider lists come from the real ADDON_PROVIDERS. sms is UI-managed
   (provider + enable only) since it has no verification backend yet. */
export interface AddonDef { id: AddonKey; name: string; tagline: string; d: string; color: string; providers: string[]; real: boolean; }
const providerNames = (id: 'gps' | 'fuel' | 'fees') => (ADDON_PROVIDERS[id] || []).map(p => p.name);
export const ADDON_CATALOG: AddonDef[] = [
  { id: 'gps', name: 'GPS tracking', tagline: 'Live vehicle positions from installed trackers', d: 'M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11zM12 10a1.5 1.5 0 1 0 0 .01', color: 'var(--info)', providers: providerNames('gps'), real: true },
  { id: 'fuel', name: 'Fuel sensor', tagline: 'Capacitive tank sensors & driver logs', d: 'M14 3v6l4 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM4 9h10', color: 'var(--amber)', providers: providerNames('fuel'), real: true },
  { id: 'fees', name: 'School fees', tagline: 'Sync invoices & reconcile payments', d: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6', color: 'var(--green)', providers: providerNames('fees'), real: true },
  { id: 'sms', name: 'SMS gateway', tagline: 'Parent alerts & OTP delivery', d: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z', color: 'var(--accent)', providers: ["Africa's Talking", 'Safaricom Bulk SMS', 'Twilio', 'Infobip'], real: false },
];
export const addonDef = (id: string): AddonDef | undefined => ADDON_CATALOG.find(a => a.id === id);

/* ── icon path constants reused across the view ── */
export const ICON = {
  revenue: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  school: 'M12 3 2 8l10 5 10-5zM2 16l10 5 10-5',
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  bus: 'M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10M4 16h16M8 8h8',
  fuel: 'M14 3v6l4 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM4 9h10',
  distance: 'M3 12h4l3 8 4-16 3 8h4',
  star: 'M12 2l3 6.9 7.1.6-5.4 4.6 1.7 7-6.4-3.8-6.4 3.8 1.7-7L2 9.5l7.1-.6z',
};

/* ── demo data (exact values from the approved design) ── */
export const DEMO_DATA: ConsoleData = {
  loading: false,
  user: { name: 'Amara Kariuki', role: 'Owner', initials: 'AK' },
  kpis: [
    { label: 'Total revenue', value: 'KES 2.84M', d: ICON.revenue, delta: '▲ 18.4%', up: true, note: 'YoY' },
    { label: 'Active schools', value: '42', d: ICON.school, delta: '▲ 6', up: true, note: '+6 this qtr' },
    { label: 'Students tracked', value: '11,860', d: ICON.users, delta: '▲ 4.2%', up: true, note: 'MoM' },
    { label: 'Fleet on road', value: '31', d: 'M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10M4 16h16M8 8h8', delta: '▼ 2', up: false, note: 'vs peak' },
  ],
  revenue: {
    points: [22, 32, 50, 44, 72, 84, 80, 108, 122, 116, 142, 164],
    months: ['Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr','May','Jun','Jul'],
    total: '2.84M', deltaLabel: '▲ 18.4% vs last year', up: true,
  },
  planMix: { premium: 19, standard: 14, trial: 9, activeSchools: 42 },
  schoolRev: [
    { name: 'Riverside Academy', amt: 'KES 624K', pct: 100 },
    { name: 'Green Valley School', amt: 'KES 512K', pct: 82 },
    { name: 'St. Monica Primary', amt: 'KES 438K', pct: 70 },
    { name: 'Nairobi Junior', amt: 'KES 361K', pct: 58 },
    { name: 'Lakeview High', amt: 'KES 287K', pct: 46 },
  ],
  activity: [
    { title: 'Riverside Academy renewed Premium', time: '12 min ago', amt: '+KES 24K', c: 'var(--green)', pos: true, d: 'M20 6L9 17l-5-5' },
    { title: 'New user invited · Green Valley', time: '38 min ago', amt: '', c: 'var(--info)', d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M20 8v6M23 11h-6' },
    { title: 'Bus KDA 214B started route', time: '1 hr ago', amt: '', c: 'var(--accent)', d: 'M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10M4 16h16' },
    { title: 'Lakeview High trial expiring', time: '3 hr ago', amt: '', c: 'var(--amber)', d: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z' },
    { title: 'Payment failed · St. Monica', time: '5 hr ago', amt: '−KES 18K', c: 'var(--red)', pos: false, d: 'M18 6 6 18M6 6l12 12' },
  ],
  orgs: [
    { name: 'Riverside Academy', code: 'RVS-4821', plan: 'Premium', status: 'Active', productType: 'FULL_FEATURE', students: '1,240', buses: '6', mrr: '52K', end: '11 Jul 2027', conns: { gps: { status: 'connected', provider: 'Karooooo (Cartrack Kenya)' }, fuel: { status: 'connected', provider: 'Technoton DUT-E' }, fees: { status: 'connected', provider: 'Zeraki Finance' }, sms: { status: 'connected', provider: "Africa's Talking" } } },
    { name: 'Green Valley School', code: 'GVL-2201', plan: 'Premium', status: 'Active', productType: 'FULL_FEATURE', students: '980', buses: '5', mrr: '44K', end: '02 Mar 2027', conns: { gps: { status: 'connected', provider: 'iTrace Africa' }, fuel: { status: 'error', provider: 'Telematics Africa' }, fees: { status: 'connected', provider: 'Zeraki Finance' }, sms: { status: 'disconnected' } } },
    { name: 'St. Monica Primary', code: 'STM-8890.bus', plan: 'Standard', status: 'Suspended', productType: 'BUS_ONLY', students: '612', buses: '3', mrr: '0', end: '18 Jan 2026', conns: { gps: { status: 'connected', provider: 'TrailMyCar' }, fuel: { status: 'disconnected' }, fees: { status: 'error', provider: 'JibuSMS' }, sms: { status: 'connected', provider: 'Safaricom Bulk SMS' } } },
    { name: 'Nairobi Junior', code: 'NRJ-1145', plan: 'Standard', status: 'Active', productType: 'FULL_FEATURE', students: '740', buses: '4', mrr: '31K', end: '30 Sep 2026', conns: { gps: { status: 'connected', provider: 'Regent Tracking' }, fuel: { status: 'connected', provider: 'Controltech EA' }, fees: { status: 'disconnected' }, sms: { status: 'connected', provider: "Africa's Talking" } } },
    { name: 'Lakeview High', code: 'LKV-3390', plan: 'Trial', status: 'Trial', productType: 'FULL_FEATURE', students: '430', buses: '2', mrr: '0', end: '25 Jul 2026', conns: {} },
    { name: 'Sunrise Kindergarten', code: 'SNR-0092', plan: 'Premium', status: 'Pending', productType: 'FULL_FEATURE', students: '210', buses: '2', mrr: '0', end: '14 Nov 2026', beta: true, conns: { gps: { status: 'connected', provider: 'NAJ Tracking' }, fuel: { status: 'disconnected' }, fees: { status: 'connected', provider: 'EBingwa' }, sms: { status: 'connected', provider: 'Twilio' } } },
    { name: 'Mombasa College', code: 'MBS-7712', plan: 'Premium', status: 'Expired', productType: 'FULL_FEATURE', students: '1,510', buses: '7', mrr: '0', end: '04 May 2026', conns: { gps: { status: 'error', provider: 'Fleetr' }, fuel: { status: 'connected', provider: 'Carro Tracking Solutions' }, fees: { status: 'connected', provider: 'SmartSoma' }, sms: { status: 'disconnected' } } },
    { name: 'Okello Family', code: 'OKELLO.family', plan: 'Families', status: 'Active', productType: 'FAMILIES', students: '2', buses: '1', mrr: '1.5K', end: '01 Oct 2026', conns: {} },
    { name: 'Wanjiku Household', code: 'WANJIKU.family', plan: 'Families', status: 'Trial', productType: 'FAMILIES', students: '1', buses: '1', mrr: '0', end: '20 Sep 2026', conns: {} },
    { name: 'Achieng Logistics', code: 'ACHIENG.cargo', plan: 'Cargo', status: 'Active', productType: 'CARGO', students: '0', buses: '4', mrr: '18K', end: '12 Dec 2026', conns: {} },
    { name: 'Kamau Hauliers', code: 'KAMAU.fleet', plan: 'Fleet', status: 'Active', productType: 'SELF_TRACKING', students: '0', buses: '2', mrr: '6K', end: '08 Nov 2026', conns: {} },
  ],
  adminGroups: [
    { school: 'Trasse Console', tag: 'Owner', admins: [
      { name: 'Amara Kariuki', email: 'amara@trasse.io', phone: '+254712000001', role: 'Owner', status: 'Active' },
      { name: 'Brian Ochieng', email: 'brian@trasse.io', phone: '+254712000002', role: 'Console admin', status: 'Active' },
    ]},
    { school: 'Riverside Academy', tag: 'RVS-4821', admins: [
      { name: 'David Otieno', email: 'd.otieno@riverside.ac.ke', phone: '+254720111222', role: 'School admin', status: 'Active' },
      { name: 'Mercy Achieng', email: 'm.achieng@riverside.ac.ke', phone: '+254720111223', role: 'Fleet admin', status: 'Active' },
    ]},
    { school: 'Green Valley School', tag: 'GVL-2201', admins: [
      { name: 'Grace Wanjiru', email: 'grace.w@greenvalley.sc.ke', phone: '+254733444555', role: 'School admin', status: 'Active' },
      { name: 'Kevin Mutua', email: 'k.mutua@greenvalley.sc.ke', phone: '+254733444556', role: 'Fleet admin', status: 'Pending' },
    ]},
    { school: 'Lakeview High', tag: 'LKV-3390 · Trial', admins: [
      { name: 'Samuel Mwangi', email: 's.mwangi@lakeview.ac.ke', phone: '+254701998877', role: 'School admin', status: 'Inactive' },
    ]},
  ],
  busGroups: [
    { school: 'Riverside Academy', code: 'RVS-4821', buses: [
      { plate: 'KDA 214B', route: 'Route 3 · Westlands loop', status: 'En route', driver: 'Peter Kamau', kids: '28', rating: 4.9, c: 'var(--green)' },
      { plate: 'KCX 902M', route: 'Route 1 · Kilimani', status: 'En route', driver: 'John Mueni', kids: '22', rating: 4.6, c: 'var(--green)' },
      { plate: 'KBZ 771A', route: 'Route 5 · Karen', status: 'Boarding', driver: 'Alice Adhiambo', kids: '9', rating: 4.8, c: 'var(--info)' },
    ]},
    { school: 'Green Valley School', code: 'GVL-2201', buses: [
      { plate: 'KDG 118T', route: 'Route 2 · Lavington', status: 'En route', driver: 'Mercy Achieng', kids: '19', rating: 4.7, c: 'var(--green)' },
      { plate: 'KBB 540H', route: 'Route 4 · Kileleshwa', status: 'Idle', driver: 'Unassigned', kids: '0', rating: 0, c: 'var(--amber)' },
    ]},
    { school: 'Nairobi Junior', code: 'NRJ-1145', buses: [
      { plate: 'KCE 337Q', route: 'Route 1 · South B', status: 'En route', driver: 'Daniel Kip', kids: '24', rating: 4.4, c: 'var(--green)' },
      { plate: 'KDL 902W', route: 'Route 2 · South C', status: 'Boarding', driver: 'Ruth Wambui', kids: '6', rating: 4.2, c: 'var(--info)' },
    ]},
  ],
  lorries: [
    { plate: 'KDF 445X', model: 'Isuzu FVR', cargo: 'Building materials', status: 'On route', fuel: '68%', dist: '312 km', c: 'var(--green)' },
    { plate: 'KCA 810P', model: 'Mitsubishi Fuso', cargo: 'Food supplies', status: 'On route', fuel: '41%', dist: '188 km', c: 'var(--green)' },
    { plate: 'KDD 226L', model: 'Isuzu NQR', cargo: 'General freight', status: 'Loading', fuel: '92%', dist: '4 km', c: 'var(--info)' },
    { plate: 'KBM 559R', model: 'Scania P360', cargo: '—', status: 'Maintenance', fuel: '12%', dist: '0 km', c: 'var(--amber)' },
    { plate: 'KDH 073V', model: 'Isuzu FRR', cargo: 'Furniture', status: 'On route', fuel: '55%', dist: '241 km', c: 'var(--green)' },
  ],
  lorryMiniStats: [ { k: 'Vehicles', v: '18' }, { k: 'On road', v: '11' }, { k: 'Avg fuel', v: '7.8 km/L' }, { k: 'Distance today', v: '2,140 km' } ],
  calendar: {
    monthLabel: 'July 2026', daysInMonth: 31, firstWeekdayOffset: 2, today: 11,
    eventColors: { 4: 'var(--accent)', 9: 'var(--info)', 15: 'var(--green)', 22: 'var(--amber)', 28: 'var(--accent)' },
    events: [
      { mon: 'Jul', day: '11', title: 'Term 2 begins', meta: 'All schools · academic', c: 'var(--accent)' },
      { mon: 'Jul', day: '15', title: 'Fleet safety inspection', meta: 'Riverside · 6 buses', c: 'var(--green)' },
      { mon: 'Jul', day: '18', title: 'Parent-teacher day', meta: 'Green Valley School', c: 'var(--info)' },
      { mon: 'Jul', day: '22', title: 'Premium renewals due', meta: '4 schools · billing', c: 'var(--amber)' },
      { mon: 'Jul', day: '28', title: 'Sports day', meta: 'Nairobi Junior', c: 'var(--accent)' },
    ],
  },
  contentItems: [
    { title: 'Term 2 Lunch Menu', meta: 'Riverside · updated 2d ago', ph: 'menu image' },
    { title: 'Bus Safety Guidelines', meta: 'Platform · PDF', ph: 'document' },
    { title: 'Sports Day Poster', meta: 'Nairobi Junior · draft', ph: 'poster' },
    { title: 'Fee Structure 2026', meta: 'All schools · published', ph: 'document' },
    { title: 'Welcome Announcement', meta: 'Green Valley · published', ph: 'banner' },
    { title: 'Holiday Calendar', meta: 'Platform · image', ph: 'image' },
  ],
  anaKpis: [
    { label: 'Avg fuel efficiency', value: '7.9 km/L', d: ICON.fuel, delta: '▲ 3.1%', up: true, note: 'vs last mo' },
    { label: 'Avg distance / day', value: '182 km', d: ICON.distance, delta: '▲ 5.4%', up: true, note: 'per vehicle' },
    { label: 'Avg driver rating', value: '4.6 / 5', d: ICON.star, delta: '▲ 0.2', up: true, note: '312 trips' },
    { label: 'Revenue lost (30d)', value: 'KES 96K', d: ICON.revenue, delta: '▼ KES 12K', up: false, note: 'churn + failed' },
  ],
  fuelPerSchool: [
    { name: 'Riverside Academy', v: '8.6 km/L', pct: 100, good: true },
    { name: 'Green Valley School', v: '8.1 km/L', pct: 88, good: true },
    { name: 'Nairobi Junior', v: '7.4 km/L', pct: 72, good: true },
    { name: 'Sunrise Kindergarten', v: '6.9 km/L', pct: 60, good: false },
    { name: 'Lakeview High', v: '6.2 km/L', pct: 46, good: false },
  ],
  driverRatings: [
    { name: 'Riverside Academy', v: 4.9, drivers: '6 drivers' },
    { name: 'Green Valley School', v: 4.7, drivers: '5 drivers' },
    { name: 'Nairobi Junior', v: 4.5, drivers: '4 drivers' },
    { name: 'Sunrise Kindergarten', v: 4.3, drivers: '2 drivers' },
    { name: 'Lakeview High', v: 3.8, drivers: '2 drivers' },
  ],
  revLost: [
    { name: 'Lakeview High', v: 'KES 42K', pct: 100 },
    { name: 'St. Monica Primary', v: 'KES 28K', pct: 67 },
    { name: 'Mombasa College', v: 'KES 18K', pct: 43 },
    { name: 'Sunrise Kindergarten', v: 'KES 8K', pct: 19 },
  ],
  distancePairs: [
    { last: 0.48, thisM: 0.62 }, { last: 0.41, thisM: 0.55 }, { last: 0.36, thisM: 0.51 },
    { last: 0.53, thisM: 0.69 }, { last: 0.44, thisM: 0.59 },
  ],
  distanceTotalLabel: 'Total fleet kilometres',
  revLostTotal: 'KES 96K total',
  notifs: [
    { title: 'Lakeview High trial expires in 2 days', time: '10 min ago', c: 'var(--amber)', d: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z' },
    { title: 'Payment failed for St. Monica Primary', time: '1 hr ago', c: 'var(--red)', d: 'M18 6 6 18M6 6l12 12' },
    { title: 'Riverside Academy renewed Premium', time: '2 hr ago', c: 'var(--green)', d: 'M20 6L9 17l-5-5' },
  ],
  estimatedPriceOrgs: [
    {
      code: 'GVL-2201', name: 'Green Valley School', plan: 'Premium',
      kind: 'regional_default', promisedEpra: true,
      detail: 'Premium fleet on a regional average instead of an EPRA ceiling',
    },
    {
      code: 'NRJ-1145', name: 'Nairobi Junior', plan: 'Standard',
      kind: 'basic_no_stations', promisedEpra: false,
      detail: 'Basic plan with no station prices entered — all costs are regional estimates',
    },
  ],
  platformAlerts: [
    {
      id: 'demo-press',
      kind: 'epra_press_update',
      severity: 'critical',
      title: 'Fuel ceilings updated from press — review',
      message: '3 press sources agreed on Nairobi diesel 222.86 / petrol 214. Ceilings were applied automatically — please verify against EPRA\'s announcement.',
      caps: { diesel: 222.86, petrol: 214 },
      acknowledged: false,
      createdAtMs: Date.now(),
    },
  ],
};
